import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { BrandMark } from '../components/BrandMark';
import { CloseIcon } from '../components/ui';
import { useMe } from '../hooks/useApiQuery';
import { useUnreadNotificationsCount } from '../hooks/useNotifications';
import { useAppPath } from '../lib/appBase';
import { logout } from '../lib/auth';
import { CAB_NAV, type CabNavItem } from './nav';

/** Map a nav key to an existing i18n label. */
function navLabel(t: (k: string) => string, key: string): string {
  switch (key) {
    case 'dashboard':
      return t('nav.dashboard');
    case 'wallet':
      return t('nav.wallet');
    case 'activity':
      return t('nav.activity');
    case 'referrals':
      return t('nav.referrals');
    case 'notifications':
      return t('menu.notifications');
    default:
      return key;
  }
}

/**
 * Web cabinet shell — a desktop dashboard, not the Telegram mini-app.
 * Fixed left sidebar on ≥lg, a slide-in drawer on mobile, and a topbar
 * carrying the current section title. Pinned to the dark walrus-navy
 * theme so the browser cabinet matches the marketing site.
 */
export function CabinetLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const ap = useAppPath();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const activeItem =
    [...CAB_NAV]
      .sort((a, b) => b.to.length - a.to.length)
      .find((it) =>
        it.endMatch
          ? location.pathname === ap(it.to)
          : location.pathname.startsWith(ap(it.to)),
      ) ?? CAB_NAV[0];

  return (
    <div
      data-theme="dark"
      className="flex min-h-screen bg-tg-bg font-sans text-tg-text"
    >
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-[248px] shrink-0 flex-col border-r border-pb-border-soft bg-pb-paper/40 px-3 py-5 backdrop-blur-xl lg:flex">
        <SidebarContent t={t} ap={ap} />
      </aside>

      {/* Mobile drawer */}
      {drawerOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="close menu"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <aside className="absolute inset-y-0 left-0 flex w-[260px] flex-col border-r border-pb-border-soft bg-tg-bg px-3 py-5">
            <button
              type="button"
              aria-label="close"
              onClick={() => setDrawerOpen(false)}
              className="pb-focus-ring absolute right-3 top-5 flex h-8 w-8 items-center justify-center rounded-full text-tg-hint hover:bg-pb-paper2"
            >
              <CloseIcon size={18} />
            </button>
            <SidebarContent t={t} ap={ap} />
          </aside>
        </div>
      ) : null}

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <CabinetTopbar
          title={navLabel(t, activeItem.key)}
          onMenu={() => setDrawerOpen(true)}
        />
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[1120px] px-4 py-6 sm:px-6 sm:py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function SidebarContent({
  t,
  ap,
}: {
  t: (k: string) => string;
  ap: (p: string) => string;
}) {
  const me = useMe();
  const navigate = useNavigate();
  const initial =
    me.data?.tg_first_name?.[0]?.toUpperCase() ??
    me.data?.tg_username?.[0]?.toUpperCase() ??
    '·';
  const name =
    me.data?.tg_first_name ??
    (me.data?.tg_username ? `@${me.data.tg_username}` : t('nav.dashboard'));

  return (
    <>
      <div className="relative px-2 pb-6">
        <span
          aria-hidden
          className="site-aurora absolute -left-4 -top-6 h-24 w-24 bg-pb-accent"
        />
        <div className="relative">
          <BrandMark size="md" />
        </div>
      </div>
      <nav className="flex flex-1 flex-col gap-1">
        {CAB_NAV.filter((it) => !it.hidden).map((it) => (
          <SidebarLink key={it.key} item={it} ap={ap} label={navLabel(t, it.key)} />
        ))}
      </nav>
      <div className="mt-4 border-t border-pb-border-soft pt-3">
        <div className="flex items-center gap-3 rounded-pb-md px-2 py-2">
          <span className="pb-display flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-pb-accent-soft text-[15px] text-pb-accent">
            {initial}
          </span>
          <span className="min-w-0 flex-1 truncate text-[14px] text-tg-text">
            {name}
          </span>
        </div>
        <button
          type="button"
          onClick={() => {
            void logout().finally(() => navigate('/login', { replace: true }));
          }}
          className="pb-focus-ring mt-1 w-full rounded-pb-md px-3 py-2 text-left text-[13px] text-tg-hint transition-colors hover:bg-pb-paper2 hover:text-tg-text"
        >
          {t('menu.logout')}
        </button>
      </div>
    </>
  );
}

function SidebarLink({
  item,
  ap,
  label,
}: {
  item: CabNavItem;
  ap: (p: string) => string;
  label: string;
}) {
  const { Icon } = item;
  return (
    <NavLink
      to={ap(item.to)}
      end={item.endMatch}
      className={({ isActive }) =>
        clsx(
          'pb-focus-ring flex items-center gap-3 rounded-pb-md px-3 py-2.5 text-[14px] font-medium transition-colors',
          isActive
            ? 'bg-pb-accent-soft text-pb-accent'
            : 'text-tg-hint hover:bg-pb-paper2 hover:text-tg-text',
        )
      }
    >
      <Icon size={19} />
      {label}
    </NavLink>
  );
}

function CabinetTopbar({
  title,
  onMenu,
}: {
  title: string;
  onMenu: () => void;
}) {
  const ap = useAppPath();
  const unread = useUnreadNotificationsCount();
  const count = unread.data?.unread_count ?? 0;
  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-pb-border-soft bg-tg-bg/85 px-4 backdrop-blur-md sm:px-6">
      <button
        type="button"
        aria-label="menu"
        onClick={onMenu}
        className="pb-focus-ring -ml-1 flex h-9 w-9 items-center justify-center rounded-pb-md text-tg-hint hover:bg-pb-paper2 lg:hidden"
      >
        <BurgerIcon />
      </button>
      <h1 className="pb-display truncate text-[20px] leading-none">{title}</h1>
      <div className="ml-auto flex items-center gap-1.5">
        <NavLink
          to={ap('/notifications')}
          aria-label="notifications"
          className="pb-focus-ring relative flex h-9 w-9 items-center justify-center rounded-full border border-pb-border-soft text-tg-hint transition-colors hover:bg-pb-paper2 hover:text-tg-text"
        >
          <BellGlyph />
          {count > 0 ? (
            <span className="pb-numeral absolute -right-0.5 -top-0.5 inline-flex min-w-[16px] items-center justify-center rounded-full bg-pb-danger px-1 text-[10px] font-semibold leading-none text-white">
              {count > 99 ? '99+' : count}
            </span>
          ) : null}
        </NavLink>
      </div>
    </header>
  );
}

function BurgerIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 7h16M4 12h16M4 17h16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function BellGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6 9a6 6 0 1 1 12 0c0 4 1.5 5 2 6H4c.5-1 2-2 2-6Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M10 19a2 2 0 0 0 4 0"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
